import { useState, useEffect, useCallback, useRef } from 'react'

export interface DemoPurchaseItem {
  id: number | string
  title: string
  price: number
  image: string
  quantity: number
}

export interface DemoPurchase {
  orderId: string
  items: DemoPurchaseItem[]
  total: number
  date: string
}

const STORAGE_PREFIX = 'dgstack_demo_purchases_'

function getStorageKey(userId: string) {
  return `${STORAGE_PREFIX}${userId}`
}

function loadPurchases(userId: string): DemoPurchase[] {
  try {
    const raw = localStorage.getItem(getStorageKey(userId))
    if (raw) return JSON.parse(raw)
  } catch {
    // ignore parse errors
  }
  return []
}

function savePurchases(userId: string, purchases: DemoPurchase[]) {
  localStorage.setItem(getStorageKey(userId), JSON.stringify(purchases))
}

export function useDemoPurchases(userId?: string) {
  const [purchases, setPurchases] = useState<DemoPurchase[]>(() => {
    // Initialize synchronously if userId is available
    if (userId) return loadPurchases(userId)
    return []
  })

  // Keep a ref to always have the latest userId (avoids stale closures in setTimeout)
  const userIdRef = useRef(userId)
  userIdRef.current = userId

  // Reload purchases when userId changes
  useEffect(() => {
    if (userId) {
      setPurchases(loadPurchases(userId))
    } else {
      setPurchases([])
    }
  }, [userId])

  const addPurchase = useCallback(
    (items: DemoPurchaseItem[], total: number) => {
      // Use ref to get the latest userId (safe inside setTimeout callbacks)
      const currentUserId = userIdRef.current
      if (!currentUserId) return null
      const newPurchase: DemoPurchase = {
        orderId: `ORD-${Date.now().toString(36).toUpperCase()}`,
        items,
        total,
        date: new Date().toISOString(),
      }
      const existing = loadPurchases(currentUserId)
      const updated = [newPurchase, ...existing]
      savePurchases(currentUserId, updated)
      setPurchases(updated)
      return newPurchase
    },
    [],
  )

  const totalPurchasedItems = purchases.reduce(
    (sum, p) => sum + p.items.reduce((s, i) => s + i.quantity, 0),
    0,
  )

  return { purchases, addPurchase, totalPurchasedItems }
}
